import React from "react";

import { Box, Grid, Tooltip, Typography } from "@material-ui/core";
import { orange } from "@material-ui/core/colors";


import {
  FunctionField,
  Labeled,
  Loading,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useRefresh,
  useShowController,
} from "react-admin";

import { ExceptionIcon } from "../../../../components/common/iconography";
import { BackButton } from "../../../../components/common/BackButton";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { HistoryTab } from "../../../../components/common/HistoryTab";
import { getUnitName } from "../../../../utils/loadTypeUtils";
import {
  shouldRefreshInventoryItemTrackCar,
  useTrackCarSubscriptionRefresh,
} from "../../../../utils/useTrackCarSubscriptionRefresh";
import { getBackPath, trimDecimal } from "../../../../utils/utils";
import { isOnHand } from "./InventoryItemUtils";
import { getLocationFullName } from "../../../../utils/locationUtils";

const InventoryItemForm = ({
  backPath,
  record,
  inventory,
  putawayQuantity,
  maxPutawayQty,
  minPutawayQty,
  ...props
}) => {
  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Labeled label="Product Name">
            <TextField source="inventory.productName" />
          </Labeled>
        </Grid>
        {!isOnHand(record) && (
          <Grid item xs={12} sm={3}>
            <Labeled label="Quantity Avaialble">
              <FunctionField
                source="quantityOnHand"
                render={() =>
                  `${maxPutawayQty} ${getUnitName(inventory.inventoryUOM)}`
                }
              />
            </Labeled>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1}>
        <Grid item container xs={12} sm={6}>
          <Grid item xs={12} sm={6}>
            <Labeled
              label={isOnHand(record) ? "Quantity On Hand" : "Putaway Quantity"}
            >
              <FunctionField
                source="quantityOnHand"
                render={() =>
                  `${record.quantityOnHand} ${getUnitName(
                    inventory.inventoryUOM,
                  )}`
                }
              />
            </Labeled>
          </Grid>
          {isOnHand(record) && (
            <Grid item xs={12} sm={6}>
              <Labeled label="Quantity Committed">
                <FunctionField
                  source="quantityCommitted"
                  render={() =>
                    `${record.quantityCommitted} ${getUnitName(
                      inventory.inventoryUOM,
                    )}`
                  }
                />
              </Labeled>
            </Grid>
          )}
        </Grid>
        {inventory.overShipment && (
          <Grid container item xs={12} sm={3} spacing={1}>
            <Grid item xs={3}>
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Tooltip
                  arrow
                  placement="left"
                  title={
                    inventory.overShipmentQuantity
                      ? `Qty. Over: ${inventory.overShipmentQuantity || ""}`
                      : "Overshipment"
                  }
                >
                  <ExceptionIcon
                    fontSize="large"
                    style={{ color: orange[600] }}
                  />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box mt={2}>
                <Typography variant="body2">
                  Overshipment
                  {inventory.overShipmentQuantity
                    ? `: ${inventory.overShipmentQuantity}`
                    : ""}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
        {inventory.underShipment && (
          <Grid container item xs={12} sm={3} spacing={1}>
            <Grid item xs={3}>
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Tooltip
                  arrow
                  placement="left"
                  title={
                    inventory.underShipmentQuantity
                      ? `Qty. Under: ${inventory.underShipmentQuantity || ""}`
                      : "Undershipment"
                  }
                >
                  <ExceptionIcon
                    fontSize="large"
                    style={{ color: orange[600] }}
                  />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box mt={2}>
                <Typography variant="body2">
                  Undershipment
                  {inventory.underShipmentQuantity
                    ? `: ${inventory.underShipmentQuantity}`
                    : ""}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        )}
        {record.otherException && (
          <Grid container item xs={12} sm={3} spacing={1}>
            <Grid item xs={3}>
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Tooltip
                  title={record.otherExceptionNote || ""}
                  placement="left"
                  arrow
                >
                  <ExceptionIcon
                    fontSize="large"
                    style={{ color: orange[600] }}
                  />
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={9}>
              <Box mt={2}>
                <Typography variant="body2">Other Exception</Typography>
              </Box>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Labeled label="Location">
            <FunctionField
              render={(record) =>
                getLocationFullName(record.location || record.locationFullName)
              }
            />
          </Labeled>
        </Grid>
        <Grid item xs={12}>
          <br />
        </Grid>
      </Grid>
    </>
  );
};

const ShowActions = (props) => (
  <TopToolbar>
    <BackButton {...props} />
  </TopToolbar>
);

export const InventoryItemShow = (props) => {
  const { record } = useShowController(props);
  const refresh = useRefresh();

  const reloadYardId = window.appUser?.reloadYardId;
  const currentInboundId =
    record?.inboundShipmentId || record?.inventory?.inboundShipmentId;
  const currentOutboundId =
    record?.outboundShipmentId || record?.inventory?.outboundShipmentId;

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(reloadYardId && record?.id),
    label: "InventoryItemShow",
    shouldRefresh: (trackCar) => {
      return shouldRefreshInventoryItemTrackCar({
        trackCar,
        reloadYardId,
        currentInboundId,
        currentOutboundId,
        recordId: record?.id,
      });
    },
    onRefresh: () => {
      setTimeout(() => refresh(), 2500);
      setTimeout(() => refresh(), 6000);
    },
  });

  const backPath = getBackPath(props);
  const inventory = props.location?.state?.inventory || record?.inventory;

  if (!record || !inventory) return <Loading />;

  const initQtyAllocated = inventory.inventoryItems?.items?.reduce(
    (total, item) => {
      return total + item.quantityOnHand;
    },
    0,
  );

  const maxPutawayQty =
    initQtyAllocated <= inventory.quantityAdvised
      ? inventory.quantityAdvised - initQtyAllocated + record?.quantityOnHand
      : 0;

  const minPutawayQty =
    record?.quantityCommitted > 0
      ? trimDecimal(record?.quantityOnHand - record?.quantityCommitted, 3)
      : 0;
  return (
    <Show
      {...props}
      title={<ForceTitle title={`View Inventory Item`} />}
      actions={<ShowActions {...props} backPath={backPath} />}
    >
      <TabbedShowLayout>
        <Tab label="Location">
          <InventoryItemForm
            {...props}
            record={record}
            inventory={inventory}
            maxPutawayQty={maxPutawayQty}
            minPutawayQty={minPutawayQty}
          />
        </Tab>
        <Tab label="History Log">
          <HistoryTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
